<template>
  <div class="space-y-6">
    <!-- 标题栏 -->
    <div class="flex items-center justify-between">
      <h2 class="text-xl font-bold text-white mb-4 flex items-center gap-2">
        <i class="ph-bold ph-desktop text-blue-400"></i>
        主机信息统计
      </h2>
      <button
        @click="$emit('refresh')"
        class="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-white transition-colors flex items-center gap-2"
      >
        <i class="ph-bold ph-arrow-clockwise"></i>
        刷新
      </button>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="glass-card p-16 rounded-xl border border-slate-700">
      <div class="flex flex-col items-center justify-center text-slate-400">
        <div class="relative w-16 h-16 mb-6">
          <!-- 旋转的圆环 -->
          <div class="absolute inset-0 rounded-full border-4 border-transparent loader-ring" style="border-top-color: #3b82f6; border-right-color: #3b82f6; border-bottom-color: #475569; border-left-color: #475569;"></div>
          <!-- 内层静态圆 -->
          <div class="absolute inset-4 bg-slate-800 rounded-full"></div>
        </div>
        <div class="flex items-center gap-2">
          <span class="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style="animation-delay: 0s"></span>
          <span class="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style="animation-delay: 0.2s"></span>
          <span class="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style="animation-delay: 0.4s"></span>
        </div>
        <p class="text-sm mt-4 animate-pulse">正在加载主机信息...</p>
      </div>
    </div>

    <!-- 主机列表（大卡片置顶） -->
    <div v-else-if="hosts && hosts.length > 0" class="space-y-6">
      <div
        v-for="host in hosts"
        :key="host.id"
        class="glass-card p-6 rounded-xl border border-slate-700 hover:border-blue-500/50 transition-all group relative overflow-hidden"
      >
        <!-- 背景装饰 -->
        <div class="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
        
        <!-- 状态指示器 -->
        <div class="absolute top-4 right-4">
          <div
            :class="[
              'w-3 h-3 rounded-full shadow-lg',
              host.status === 'online' ? 'bg-green-500 animate-pulse' : 'bg-red-500'
            ]"
          ></div>
        </div>

        <!-- 主机名和IP -->
        <div class="mb-6">
      <div class="mb-2 flex items-start gap-3">
        <div class="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-400/45 via-cyan-400/35 to-purple-500/35 border border-white/12 shadow-lg shadow-blue-700/30 flex items-center justify-center">
          <i class="ph-bold ph-desktop-tower text-white text-xl drop-shadow-[0_6px_12px_rgba(59,130,246,0.55)]"></i>
        </div>
            <div class="flex-1">
              <h3 class="text-lg font-bold text-white">{{ host.hostname || '未知主机' }}</h3>
              <p class="text-sm text-slate-400 flex items-center gap-2 mt-1">
                <i class="ph-bold ph-globe text-xs"></i>
                {{ host.ip || '127.0.0.1' }}
              </p>
            </div>
          </div>
          
          <!-- 状态标签 -->
          <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium"
               :class="host.status === 'online' 
                 ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                 : 'bg-red-500/20 text-red-400 border border-red-500/30'">
            <div :class="host.status === 'online' ? 'bg-green-500' : 'bg-red-500'" class="w-2 h-2 rounded-full"></div>
            {{ host.status === 'online' ? '在线' : '离线' }}
          </div>
        </div>

        <!-- 详细信息网格 -->
        <div class="grid grid-cols-2 gap-4">
          <!-- 操作系统 -->
          <div class="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
            <div class="flex items-center gap-2 mb-2">
              <i :class="[getOsIcon(host.os), 'text-blue-400 text-sm']"></i>
              <span class="text-xs text-slate-400 font-medium">操作系统</span>
            </div>
            <p class="text-white font-semibold text-sm">{{ host.os && host.os !== 'Unknown' ? host.os : '未知' }}</p>
            <p class="text-slate-400 text-xs mt-1">{{ host.osVersion && host.osVersion !== 'Unknown' ? host.osVersion : '未知版本' }}</p>
          </div>

          <!-- CPU核数 -->
          <div class="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
            <div class="flex items-center gap-2 mb-2">
              <i class="ph-bold ph-cpu text-blue-400 text-sm"></i>
              <span class="text-xs text-slate-400 font-medium">CPU核数</span>
            </div>
            <p class="text-white font-semibold text-lg">{{ host.cpuCores != null && host.cpuCores !== undefined ? host.cpuCores : '未知' }}</p>
            <p class="text-slate-400 text-xs mt-1">逻辑核心</p>
          </div>

          <!-- 总内存 -->
          <div class="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
            <div class="flex items-center gap-2 mb-2">
              <i class="ph-bold ph-memory text-blue-400 text-sm"></i>
              <span class="text-xs text-slate-400 font-medium">总内存</span>
            </div>
            <p class="text-white font-semibold text-sm">{{ host.totalMemory != null && host.totalMemory !== undefined && host.totalMemory > 0 ? formatFileSize(host.totalMemory) : '未知' }}</p>
            <p class="text-slate-400 text-xs mt-1">物理内存</p>
          </div>

          <!-- 总磁盘 -->
          <div class="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
            <div class="flex items-center gap-2 mb-2">
              <i class="ph-bold ph-hard-drive text-blue-400 text-sm"></i>
              <span class="text-xs text-slate-400 font-medium">总磁盘</span>
            </div>
            <p class="text-white font-semibold text-sm">{{ host.totalDisk != null && host.totalDisk !== undefined && host.totalDisk > 0 ? formatFileSize(host.totalDisk) : '未知' }}</p>
            <p class="text-slate-400 text-xs mt-1">存储空间</p>
          </div>
        </div>

        <!-- Smart Grid版本 -->
        <div class="mt-4 pt-4 border-t border-slate-700/50">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <i class="ph-bold ph-code text-slate-400 text-sm"></i>
              <span class="text-xs text-slate-400">Smart Grid版本</span>
            </div>
            <span class="text-xs font-medium text-blue-400 bg-blue-500/10 px-2 py-1 rounded">
              {{ host.agentVersion || '1.0.0' }}
            </span>
          </div>
        </div>
      </div>

      <!-- 资源监控小卡片（置底一行） -->
      <div
        v-if="summaryCards.length"
        class="grid grid-cols-1 lg:grid-cols-3 gap-4"
      >
        <div
          v-for="card in summaryCards"
          :key="card.id"
          class="glass-card p-4 rounded-xl border border-slate-700/70 relative overflow-hidden group shadow-lg shadow-blue-900/20"
        >
          <div
            class="absolute inset-0 opacity-0 group-hover:opacity-70 transition-opacity duration-300"
            :class="card.bg"
          ></div>
          <div class="relative flex items-center justify-between mb-3">
            <div class="flex items-center gap-2">
              <div
                class="w-11 h-11 rounded-2xl flex items-center justify-center text-white/90 shadow-lg ring-1 ring-white/12 bg-gradient-to-br"
                :class="card.iconBg"
              >
                <i :class="card.icon" class="drop-shadow-[0_6px_12px_rgba(59,130,246,0.35)]"></i>
              </div>
              <div>
                <p class="text-xs text-slate-400">{{ card.subtitle }}</p>
                <p class="text-sm font-semibold text-white">{{ card.title }}</p>
              </div>
            </div>
            <div class="text-right">
              <div :class="['text-2xl font-bold leading-tight', usageClass(card)]">
                {{ card.usage.toFixed(1) }}%
              </div>
              <p class="text-[11px] text-slate-400">阈值 {{ card.displayThreshold.toFixed(0) }}%</p>
            </div>
          </div>

          <div class="relative">
            <div class="w-full bg-slate-800 rounded-full h-3 overflow-hidden border border-slate-700/70">
              <div
                class="h-full w-full rounded-full transition-all duration-500 relative overflow-hidden origin-left"
                :class="barClass(card)"
                :style="{ transform: `scaleX(${Math.min(100, Math.max(0, card.usage)) / 100})`, willChange: 'transform' }"
              >
                <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
              </div>
            </div>
            <div class="flex items-center justify-between mt-2 text-[11px] text-slate-400">
              <span>{{ card.leftLabel }}</span>
              <span :class="usageClass(card)">{{ card.midLabel }}</span>
              <span>{{ card.rightLabel }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else class="glass-card p-12 rounded-xl border border-slate-700">
      <div class="flex flex-col items-center justify-center text-slate-400">
        <div class="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mb-4">
          <i class="ph-bold ph-desktop text-3xl text-slate-600"></i>
        </div>
        <p class="text-sm font-medium mb-1">暂无主机数据</p>
        <p class="text-xs text-slate-500">请检查数据库连接或Agent配置</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  hosts: { type: Array, default: () => [] },
  formatFileSize: { type: Function, required: true },
  loading: { type: Boolean, default: false },
  diskMonitor: { type: Object, default: null },
  memoryMonitor: { type: Object, default: null },
  cpuLoad: { type: Object, default: null }
});

defineEmits(['refresh']);

const getEffectiveThreshold = (threshold) => {
  const num = Number(threshold);
  if (isNaN(num) || num <= 5) return 80; // 合理缺省，避免 1% 之类导致误判
  return num;
};

const usageClass = (card) => {
  const th = getEffectiveThreshold(card.displayThreshold);
  if (card.usage >= th) return 'text-red-400';
  if (card.usage >= th - 10) return 'text-yellow-300';
  return 'text-emerald-300';
};

const barClass = (card) => {
  const th = getEffectiveThreshold(card.displayThreshold);
  if (card.usage >= th) return 'bg-gradient-to-r from-red-500 to-red-600';
  if (card.usage >= th - 10) return 'bg-gradient-to-r from-amber-400 to-amber-500';
  return 'bg-gradient-to-r from-emerald-400 to-cyan-400';
};

const summaryCards = computed(() => {
  const cards = [];
  const fs = props.formatFileSize;

  if (props.cpuLoad) {
    const usage = Number(props.cpuLoad.usage ?? props.cpuLoad.load ?? 0);
    const thresholdRaw = props.cpuLoad.threshold;
    const displayThreshold = getEffectiveThreshold(thresholdRaw);
    cards.push({
      id: 'cpu',
      title: 'CPU 负载',
      subtitle: '实时',
      icon: 'ph-bold ph-cpu text-white',
      iconBg: 'from-emerald-500/40 via-cyan-400/30 to-teal-400/30 border border-emerald-300/30',
      bg: 'bg-gradient-to-br from-emerald-500/10 via-cyan-500/5 to-transparent',
      usage: isNaN(usage) ? 0 : usage,
      displayThreshold,
      leftLabel: `${props.cpuLoad.cores ? props.cpuLoad.cores + '核' : ''}`.trim() || '多核',
      midLabel: props.cpuLoad.alert ? '告警' : '正常',
      rightLabel: props.cpuLoad.loadAverage ? `负载 ${props.cpuLoad.loadAverage}` : '实时监控'
    });
  }

  if (props.memoryMonitor) {
    const usage = Number(props.memoryMonitor.usage ?? 0);
    const displayThreshold = getEffectiveThreshold(props.memoryMonitor.threshold);
    cards.push({
      id: 'memory',
      title: '内存监控',
      subtitle: '使用率',
      icon: 'ph-bold ph-memory text-white',
      iconBg: 'from-green-500/40 via-emerald-400/35 to-teal-400/25 border border-green-300/30',
      bg: 'bg-gradient-to-br from-green-500/10 via-emerald-500/5 to-transparent',
      usage: isNaN(usage) ? 0 : usage,
      displayThreshold,
      leftLabel: fs ? fs(props.memoryMonitor.free || 0) + ' 可用' : '可用',
      midLabel: fs ? fs(props.memoryMonitor.used || 0) + ' 已用' : '已用',
      rightLabel: fs ? fs(props.memoryMonitor.total || 0) + ' 总计' : '总计'
    });
  }

  if (props.diskMonitor) {
    const usage = Number(props.diskMonitor.usage ?? 0);
    const displayThreshold = getEffectiveThreshold(props.diskMonitor.threshold);
    cards.push({
      id: 'disk',
      title: '磁盘监控',
      subtitle: '空间',
      icon: 'ph-bold ph-hard-drive text-white',
      iconBg: 'from-indigo-500/40 via-blue-500/35 to-purple-500/30 border border-indigo-300/30',
      bg: 'bg-gradient-to-br from-indigo-500/10 via-blue-500/5 to-transparent',
      usage: isNaN(usage) ? 0 : usage,
      displayThreshold,
      leftLabel: fs ? fs((props.diskMonitor.total || 0) - (props.diskMonitor.used || 0)) + ' 可用' : '可用',
      midLabel: fs ? fs(props.diskMonitor.used || 0) + ' 已用' : '已用',
      rightLabel: fs ? fs(props.diskMonitor.total || 0) + ' 总计' : '总计'
    });
  }

  return cards;
});

const getOsIcon = (os) => {
  if (!os) return 'ph-bold ph-device-desktop';
  const name = String(os).toLowerCase();
  if (name.includes('linux')) return 'ph-bold ph-linux-logo';
  if (name.includes('windows')) return 'ph-bold ph-windows-logo';
  if (name.includes('mac') || name.includes('os x')) return 'ph-bold ph-apple-logo';
  return 'ph-bold ph-device-desktop';
};
</script>

<style scoped>
.glass-card {
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.loader-ring {
  animation: spin 1s linear infinite;
}

.animate-shimmer {
  background-size: 200% 100%;
  animation: shimmer 1.5s linear infinite;
}

@keyframes shimmer {
  0% {
    transform: translateX(-50%);
  }
  100% {
    transform: translateX(50%);
  }
}
</style>
